import React, { useState } from "react";
import { useParams } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import {
  EvidenceBlock,
  JobFacts,
  JobHeader,
  KIND_LABEL,
  PtError,
  PtLoading,
  PtPage,
  ScopeBlock,
  StateBox,
  StatusPill,
  fmtDateTime,
  money,
  parseAmount,
  postPublic,
  textOf,
  usePublicTurnover,
} from "./turnoverShared";

const hasValue = (v) => v !== undefined && v !== null && v !== "";

/** The vendor's quote, set against the property's own estimate when there is one. */
const QuoteCard = ({ job }) => {
  const q = job.quote || {};
  const vendor = job.vendor_name || job.vendor_email || "The vendor";
  const est = hasValue(job.estimate_price) ? Number(job.estimate_price) : null;
  const amt = hasValue(q.amount) ? Number(q.amount) : null;
  let diff = null;
  if (est !== null && amt !== null && est > 0) {
    const delta = amt - est;
    const pct = Math.round((Math.abs(delta) / est) * 100);
    diff =
      delta === 0
        ? "matches the estimate"
        : `${delta > 0 ? "+" : "−"}${money(Math.abs(delta))} (${pct}%) vs. estimate`;
  }

  return (
    <div className="pt-quote">
      <div>
        <span className="pt-label">Vendor&rsquo;s quote</span>
        <div className="pt-quote-amount">{money(q.amount)}</div>
        <div className="pt-meta">
          {vendor}
          {job.vendor_name && job.vendor_email ? ` · ${job.vendor_email}` : ""}
        </div>
        {q.submitted_at ? <div className="pt-hint">Sent {fmtDateTime(q.submitted_at)}</div> : null}
      </div>
      <div>
        {est !== null ? (
          <>
            <span className="pt-label">Property&rsquo;s estimate</span>
            <div className="pt-quote-ref">
              {money(est)}
              {diff ? <span className="pt-quote-diff">{diff}</span> : null}
            </div>
          </>
        ) : null}
        {q.notes ? (
          <>
            <span className="pt-label">Vendor notes</span>
            <p className="pt-prose">{q.notes}</p>
          </>
        ) : null}
      </div>
    </div>
  );
};

/**
 * Approve / Decline. Approve opens an inline confirm with the amount prefilled from the
 * quote and an optional note; Decline needs a reason. `onDecided(decision, data, payload)`
 * gets the server's response; `onStale(err)` fires on 409/401/404 so the parent re-reads.
 */
const DecisionPanel = ({ job, url, onDecided, onStale }) => {
  const q = job.quote || {};
  const [mode, setMode] = useState(null); // null | "approve" | "decline"
  const [amount, setAmount] = useState(hasValue(q.amount) ? String(q.amount) : "");
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const open = (next) => {
    setMode(next);
    setError("");
  };

  const send = async (payload) => {
    setBusy(true);
    setError("");
    try {
      const res = await postPublic(url, payload);
      onDecided(payload.decision, res && res.data, payload);
    } catch (err) {
      if (err.status === 409 || err.status === 401 || err.status === 404) {
        onStale(err);
        return;
      }
      setError(err.message || "That did not go through. Please try again.");
      setBusy(false);
    }
  };

  const approve = (e) => {
    e.preventDefault();
    const payload = { decision: "approve" };
    if (amount !== "") {
      const n = parseAmount(amount);
      if (n === null) {
        setError("The approved amount must be a positive number, or leave it blank to approve the quoted amount.");
        return;
      }
      payload.amount = n;
    }
    if (note.trim()) payload.note = note.trim();
    send(payload);
  };

  const decline = (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError("Please give the vendor a short reason.");
      return;
    }
    send({ decision: "decline", reason: reason.trim() });
  };

  if (mode === null) {
    return (
      <div className="pt-actions">
        <button type="button" className="btn btn-primary" onClick={() => open("approve")}>
          <FeatherIcon icon="check" size={15} />
          Approve
        </button>
        <button type="button" className="btn btn-outline-danger" onClick={() => open("decline")}>
          <FeatherIcon icon="x" size={15} />
          Decline
        </button>
      </div>
    );
  }

  if (mode === "approve") {
    const shown = parseAmount(amount);
    return (
      <form className="pt-panel" onSubmit={approve} noValidate>
        <h3 className="pt-panel-title">Approve this estimate</h3>
        <div className="pt-form-grid">
          <div>
            <label className="form-label" htmlFor="pt-approve-amount">
              Approved amount (CAD)
            </label>
            <div className="input-group">
              <span className="input-group-text">$</span>
              <input
                id="pt-approve-amount"
                className="form-control"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                autoFocus
              />
            </div>
            <p className="pt-hint">Prefilled with the vendor&rsquo;s quote. Change it only if you agreed a different price.</p>
          </div>
          <div>
            <label className="form-label" htmlFor="pt-approve-note">
              Note <span className="pt-optional">(optional)</span>
            </label>
            <textarea
              id="pt-approve-note"
              className="form-control"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Anything the property team or the vendor should know"
            />
          </div>
        </div>
        {error ? <p className="pt-form-error">{error}</p> : null}
        <div className="pt-actions">
          <button type="submit" className="btn btn-primary" disabled={busy}>
            <FeatherIcon icon="check" size={15} />
            {busy ? "Approving…" : `Confirm approval${shown !== null ? ` · ${money(shown)}` : ""}`}
          </button>
          <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={() => open(null)}>
            Back
          </button>
        </div>
      </form>
    );
  }

  return (
    <form className="pt-panel is-danger" onSubmit={decline} noValidate>
      <h3 className="pt-panel-title">Decline this estimate</h3>
      <label className="form-label" htmlFor="pt-decline-reason">
        Reason <span className="pt-required">(required)</span>
      </label>
      <textarea
        id="pt-decline-reason"
        className="form-control"
        rows={3}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="The vendor and the property team will see this"
        required
        autoFocus
      />
      {error ? <p className="pt-form-error">{error}</p> : null}
      <div className="pt-actions">
        <button type="submit" className="btn btn-danger" disabled={busy}>
          <FeatherIcon icon="x" size={15} />
          {busy ? "Declining…" : "Confirm decline"}
        </button>
        <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={() => open(null)}>
          Back
        </button>
      </div>
    </form>
  );
};

/** What the approver sees once the assignment is no longer waiting on them. */
const DecisionOutcome = ({ job, decided }) => {
  const q = job.quote || {};
  const a = job.approval || {};
  const d = job.declined || {};
  const vendor = job.vendor_name || job.vendor_email || "The vendor";

  if (decided) {
    return decided.decision === "approve" ? (
      <StateBox tone="success" icon="check-circle" title={`Approved at ${money(decided.amount)}`}>
        <p>Your decision has been recorded; the property team can see it in Rentdigicare.</p>
        {decided.note ? <p className="pt-hint">Your note: {decided.note}</p> : null}
      </StateBox>
    ) : (
      <StateBox tone="danger" icon="x-circle" title="Estimate declined">
        <p>Your decision has been recorded; the property team can see it in Rentdigicare.</p>
        {decided.reason ? <p className="pt-hint">Reason: {decided.reason}</p> : null}
      </StateBox>
    );
  }

  switch (job.status) {
    case "assigned":
      return (
        <StateBox tone="info" icon="clock" title="No estimate yet">
          <p>{vendor} has not sent an estimate for this job yet. There is nothing to approve until they do.</p>
        </StateBox>
      );
    case "approved":
      return (
        <StateBox
          tone="success"
          icon="check-circle"
          title={`Approved at ${money(hasValue(a.amount) ? a.amount : q.amount)}`}
        >
          <p>
            {a.by_name || a.by_email ? `Approved by ${a.by_name || a.by_email}` : "Approved"}
            {a.at ? ` on ${fmtDateTime(a.at)}` : ""}.
          </p>
          {a.note ? <p className="pt-hint">Note: {a.note}</p> : null}
        </StateBox>
      );
    case "declined":
      return (
        <StateBox tone="danger" icon="x-circle" title="Estimate declined">
          <p>
            {d.by_name || d.by_email ? `Declined by ${d.by_name || d.by_email}` : "Declined"}
            {d.at ? ` on ${fmtDateTime(d.at)}` : ""}.
          </p>
          {d.reason ? <p className="pt-hint">Reason: {d.reason}</p> : null}
        </StateBox>
      );
    case "cancelled":
      return (
        <StateBox tone="muted" icon="slash" title="This request was cancelled">
          <p>{(job.cancelled && job.cancelled.reason) || "The property withdrew this assignment. No decision is needed."}</p>
        </StateBox>
      );
    default:
      return null;
  }
};

/** /turnover/approve/:id/:aid/:token - the approver's decision page, no login. */
const TurnoverApprovePage = () => {
  const { id, aid, token } = useParams();
  const { loading, error, data: job, reload, url } = usePublicTurnover("approve", id, aid, token);
  const [decided, setDecided] = useState(null);
  const [staleMessage, setStaleMessage] = useState("");

  const handleDecided = (decision, data, payload) => {
    const q = (job && job.quote) || {};
    let approvedAmount = q.amount;
    if (data && data.approval && hasValue(data.approval.amount)) approvedAmount = data.approval.amount;
    else if (hasValue(payload.amount)) approvedAmount = payload.amount;
    setDecided({ decision, amount: approvedAmount, note: payload.note, reason: payload.reason });
  };

  const handleStale = (err) => {
    setStaleMessage(err.status === 409 ? err.message : "");
    reload();
  };

  let body;
  if (loading) {
    body = <PtLoading text="Loading the estimate…" />;
  } else if (error) {
    body = <PtError error={error} />;
  } else {
    const kind = KIND_LABEL[job.kind] || "Turnover";
    const company = textOf(job.company);
    let effectiveStatus = job.status;
    if (decided) effectiveStatus = decided.decision === "approve" ? "approved" : "declined";
    body = (
      <>
        <JobHeader job={job} eyebrow={`${kind} turnover${company ? ` · ${company}` : ""}`} title="Approval request">
          <div className="pt-status-row">
            <StatusPill status={effectiveStatus} />
            {job.approver_email ? <span className="pt-meta">Sent to {job.approver_email}</span> : null}
          </div>
        </JobHeader>

        <JobFacts job={job} />

        <section className="pt-section">
          <h2 className="pt-section-title">
            <FeatherIcon icon="dollar-sign" size={16} />
            Estimate
          </h2>
          {job.quote ? <QuoteCard job={job} /> : null}
          {staleMessage ? (
            <StateBox tone="warning" icon="info" title="This request has changed since the page was opened">
              <p>{staleMessage}</p>
            </StateBox>
          ) : null}
          {!decided && job.status === "quoted" ? (
            <DecisionPanel job={job} url={url} onDecided={handleDecided} onStale={handleStale} />
          ) : (
            <DecisionOutcome job={job} decided={decided} />
          )}
        </section>

        <ScopeBlock scope={job.scope} />
        <EvidenceBlock job={job} />
      </>
    );
  }

  return <PtPage footer="This link is personal to you. Please do not forward it.">{body}</PtPage>;
};

export { TurnoverApprovePage };
export default TurnoverApprovePage;
